// An asset's generations: every source it has had, and which one it is at.

import { api } from '../client'
import type { components } from '../schema'

export type AssetVersionsView = components['schemas']['AssetVersionsView']
export type AssetVersionEntry = components['schemas']['AssetVersionEntry']
export type AssetVersionDeleted = components['schemas']['AssetVersionDeleted']
export type AssetVersionRestored = components['schemas']['AssetVersionRestored']
export type AssetVersionPurged = components['schemas']['AssetVersionPurged']
export type SetAssetVersionRequest = components['schemas']['SetAssetVersionRequest']
export type AssetVersionChanged = components['schemas']['AssetVersionChanged']

/**
 * Every generation of an asset, newest first — a dropped one included, as a marked row rather
 * than a gap in the numbering.
 */
export function listAssetVersions(assetId: string, signal?: AbortSignal): Promise<AssetVersionsView> {
  return api.get<AssetVersionsView>(`/assets/${encodeURIComponent(assetId)}/versions`, undefined, signal)
}

/** Put an asset back on one of its generations; its size becomes that generation's. */
export function setAssetVersion(assetId: string, request: SetAssetVersionRequest): Promise<AssetVersionChanged> {
  return api.put<AssetVersionChanged>(`/assets/${encodeURIComponent(assetId)}/version`, request)
}

/**
 * Bring a dropped generation back out of the trash (`billing-ui.md` U12). Idempotent: a generation
 * already live answers `files_restored: 0`; one dropped more than thirty days ago, or deleted for
 * good, is `404` (U33).
 */
export function restoreAssetVersion(assetId: string, version: number): Promise<AssetVersionRestored> {
  return api.post<AssetVersionRestored>(
    `/assets/${encodeURIComponent(assetId)}/versions/${version}/restorations`,
  )
}

/**
 * Drop one generation's files into the trash. 409 for the generation the asset is at: move it
 * elsewhere first.
 */
export function deleteAssetVersion(assetId: string, version: number): Promise<AssetVersionDeleted> {
  return api.del<AssetVersionDeleted>(`/assets/${encodeURIComponent(assetId)}/versions/${version}`)
}

/**
 * Delete a dropped generation for good (`billing-ui.md` U33): irreversible, and it stops being
 * billed. 409 for a live generation (drop it first), 404 for one already deleted for good, 403 for
 * a caller without `assets.asset.delete` or held by the pool's lock.
 */
export function purgeAssetVersion(assetId: string, version: number): Promise<AssetVersionPurged> {
  return api.del<AssetVersionPurged>(
    `/assets/${encodeURIComponent(assetId)}/versions/${version}/files`,
  )
}
